import { useState, useCallback, useRef } from 'react';
import salesAnalyticsService from '../services/salesAnalyticsService';
import i18n from '../i18n';

const useSalesAnalytics = () => {
    const [summary, setSummary] = useState(null);
    const [orders, setOrders] = useState([]);
    const [ordersTotal, setOrdersTotal] = useState(0);
    const [dailyData, setDailyData] = useState([]);
    const [leaderboard, setLeaderboard] = useState([]);
    const [fetching, setFetching] = useState(false);
    const [error, setError] = useState(null);

    const ordersAbortRef = useRef(null);
    const dailyAbortRef = useRef(null);
    const leaderboardAbortRef = useRef(null);

    const fetchSummary = useCallback(async (params = {}) => {
        setFetching(true);
        setError(null);
        try {
            const result = await salesAnalyticsService.getSummary(params);
            if (result.success) {
                setSummary(result.data);
            } else {
                setError(result.error);
                setSummary(null);
            }
            return result;
        } catch (err) {
            setError(err.message || i18n.t('hookErrors.salesAnalytics.fetchSummary'));
        } finally {
            setFetching(false);
        }
    }, []);

    const fetchOrders = useCallback(async (params = {}) => {
        if (ordersAbortRef.current) {
            ordersAbortRef.current.abort();
        }
        const controller = new AbortController();
        ordersAbortRef.current = controller;

        setFetching(true);
        setError(null);
        try {
            const result = await salesAnalyticsService.getOrders(params, { signal: controller.signal });
            if (result.isCanceled) return;
            if (result.success) {
                const raw = result.data;
                setOrders(raw?.orders ?? raw?.items ?? []);
                setOrdersTotal(raw?.total ?? 0);
            } else {
                setError(result.error);
                setOrders([]);
            }
            return result;
        } catch (err) {
            setError(err.message || i18n.t('hookErrors.orders.fetchList'));
        } finally {
            if (ordersAbortRef.current === controller) {
                setFetching(false);
                ordersAbortRef.current = null;
            }
        }
    }, []);

    const fetchDaily = useCallback(async (params = {}) => {
        if (dailyAbortRef.current) {
            dailyAbortRef.current.abort();
        }
        const controller = new AbortController();
        dailyAbortRef.current = controller;

        setFetching(true);
        setError(null);
        try {
            const result = await salesAnalyticsService.getDaily(params, { signal: controller.signal });
            if (result.isCanceled) return;
            if (result.success) {
                const raw = result.data;
                setDailyData(raw?.daily ?? raw?.items ?? (Array.isArray(raw) ? raw : []));
            } else {
                setError(result.error);
                setDailyData([]);
            }
            return result;
        } catch (err) {
            setError(err.message || i18n.t('hookErrors.salesAnalytics.fetchDaily'));
        } finally {
            if (dailyAbortRef.current === controller) {
                setFetching(false);
                dailyAbortRef.current = null;
            }
        }
    }, []);

    const fetchLeaderboard = useCallback(async (params = {}) => {
        if (leaderboardAbortRef.current) {
            leaderboardAbortRef.current.abort();
        }
        const controller = new AbortController();
        leaderboardAbortRef.current = controller;

        setFetching(true);
        setError(null);
        try {
            const result = await salesAnalyticsService.getLeaderboard(params, { signal: controller.signal });
            if (result.isCanceled) return;
            if (result.success) {
                const raw = result.data;
                setLeaderboard(raw?.sales_staff ?? raw?.leaderboard ?? raw?.items ?? (Array.isArray(raw) ? raw : []));
            } else {
                setError(result.error);
                setLeaderboard([]);
            }
            return result;
        } catch (err) {
            setError(err.message || i18n.t('hookErrors.salesAnalytics.fetchLeaderboard'));
        } finally {
            if (leaderboardAbortRef.current === controller) {
                setFetching(false);
                leaderboardAbortRef.current = null;
            }
        }
    }, []);

    return {
        summary,
        orders,
        ordersTotal,
        dailyData,
        leaderboard,
        fetching,
        error,
        fetchSummary,
        fetchOrders,
        fetchDaily,
        fetchLeaderboard,
    };
};

export default useSalesAnalytics;
